import React, { useEffect, useState } from "react";
import { PushSettings } from "./push-settings.tsx";
import { OfflineSettings } from "./offline-settings.tsx";
import type { User } from "../shared/contracts.ts";
import {
  displayFraction,
  type Profile,
  type ProgressConfig,
  type ProgressRow,
} from "../shared/progress.ts";
import { api, Banner, Button, Field } from "./ui.tsx";
import "./progress.css";
import { ExactCount } from "./exact-count.tsx";
type Board = {
  available: boolean;
  reason: string | null;
  items: ProgressRow[];
  config: ProgressConfig;
  pending: number;
};
const initials = (name: string) =>
  name
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("");
function Person({
  row,
  user,
  category,
  digits,
}: {
  row: ProgressRow;
  user: User;
  category: string;
  digits: number;
}) {
  return (
    <>
      <span className="progress-avatar" aria-hidden="true">
        {initials(row.name)}
      </span>
      <strong>
        {row.name}
        {row.id === user.id && <small>Você</small>}
      </strong>
      <span className="progress-number">
        {category === "demands" ? (
          row.demands
        ) : category === 'visits' ? (
          row.visits ?? 0
        ) : category === "levels" ? (
          (row.level?.name ?? "Nível não configurado")
        ) : (
          <ExactCount
            value={row.points}
            digits={digits}
            scope={`ranking:${user.id}:${row.id}:points`}
            label="pontos"
          />
        )}
      </span>
      <span className="muted">
        {category === "demands"
          ? "demandas encerradas"
          : category === 'visits' ? 'primeiras visitas'
          : category === "levels"
            ? displayFraction(row.xp, digits) + " XP"
            : "pontos"}
      </span>
    </>
  );
}
export function Rankings({
  user,
  notify,
}: {
  user: User;
  notify: (s: string) => void;
}) {
  const [category, setCategory] = useState("points"),
    [board, setBoard] = useState<Board | null>(null),
    [error, setError] = useState(""),
    [retry, setRetry] = useState(0),
    [settings, setSettings] = useState(false);
  useEffect(() => {
    const abort = new AbortController();
    setBoard(null);
    setError("");
    api<Board>(
      "/progress/rankings?category=" + category,
      "GET",
      undefined,
      abort.signal,
    )
      .then((b) => {
        if (!abort.signal.aborted) setBoard(b);
      })
      .catch((e) => {
        if (!abort.signal.aborted) setError(e.message);
      });
    return () => abort.abort();
  }, [category, retry]);
  return (
    <section className="progress-page">
      <div className="progress-heading">
        <div>
          <h1>Rankings</h1>
          <p>Resultados registrados · todo o período</p>
        </div>
        {user.admin && (
          <Button onClick={() => setSettings(!settings)}>
            {settings ? "Fechar configuração" : "Configurar"}
          </Button>
        )}
      </div>
      {settings && (
        <ProgressSettings
          onSaved={() => {
            setRetry((n) => n + 1);
            notify("Regras do ranking publicadas.");
          }}
        />
      )}
      <div
        className="progress-tabs"
        role="tablist"
        aria-label="Categoria do ranking"
      >
        {[
          ["points", "Pontos"],
          ["levels", "Níveis"],
          ["demands", "Demandas"],
          ["visits", "Visitas"],
        ].map(([id, label]) => (
          <button
            key={id}
            role="tab"
            aria-selected={category === id}
            onClick={() => setCategory(id)}
          >
            {label}
          </button>
        ))}
      </div>
      {error && (
        <Banner kind="danger">
          {error}
          <Button onClick={() => setRetry((n) => n + 1)}>
            Tentar novamente
          </Button>
        </Banner>
      )}
      {!board && !error && <p role="status">Consultando resultados…</p>}
      {board &&
        (!board.available ? (
          <Banner>{board.reason}</Banner>
        ) : (
          <>
            {board.reason && <Banner kind="warn">{board.reason}</Banner>}
            {!board.items.length ? (
              <p>Nenhum participante selecionado pelo administrador.</p>
            ) : (
              <>
                <p className="muted">
                  {board.items.length} participantes · empates compartilham a
                  posição
                </p>
                <ol className="progress-podium">
                  {board.items.slice(0, 3).map((row, i) => (
                    <li key={row.id} className={"podium-slot-" + i}>
                      {row.rank === 1 && (
                        <span aria-hidden="true" className="progress-crown">
                          ♛
                        </span>
                      )}
                      <span className="sr-only">Posição {row.rank}</span>
                      <Person
                        row={row}
                        user={user}
                        category={category}
                        digits={board.config.digits}
                      />
                      <span className="progress-place">{row.rank}º</span>
                    </li>
                  ))}
                </ol>
                <ol className="progress-rest" start={4}>
                  {board.items.slice(3).map((row) => (
                    <li
                      key={row.id}
                      className={row.id === user.id ? "is-me" : ""}
                    >
                      <span>{row.rank}º</span>
                      <div>
                        <Person
                          row={row}
                          user={user}
                          category={category}
                          digits={board.config.digits}
                        />
                      </div>
                    </li>
                  ))}
                </ol>
              </>
            )}
          </>
        ))}
      <p className="muted small">
        Pontos e XP são grandezas distintas. A precisão muda apenas a
        apresentação; a divisão é conservada no cálculo. Importação de planilhas
        não concede recompensas.
      </p>
    </section>
  );
}
function ProgressSettings({ onSaved }: { onSaved: () => void }) {
  const [data, setData] = useState<{
      config: ProgressConfig;
      users: Array<{ id: string; name: string }>;
    } | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    const abort = new AbortController();
    api("/progress/config", "GET", undefined, abort.signal)
      .then(setData)
      .catch((e) => {
        if (!abort.signal.aborted) setError(e.message);
      });
    return () => abort.abort();
  }, []);
  return (
    <section className="progress-card">
      <h2>Participantes e níveis</h2>
      {error && <Banner kind="danger">{error}</Banner>}
      {data && (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError("");
            try {
              const config = await api<ProgressConfig>(
                "/progress/config",
                "PUT",
                data.config,
              );
              setData({ ...data, config });
              onSaved();
            } catch (e) {
              setError((e as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          <label>
            <input
              type="checkbox"
              checked={data.config.enabled}
              onChange={(e) =>
                setData({
                  ...data,
                  config: { ...data.config, enabled: e.target.checked },
                })
              }
            />{" "}
            Publicar rankings
          </label>
          <fieldset>
            <legend>Quem participa</legend>
            {data.users.map((u) => (
              <label className="progress-check" key={u.id}>
                <input
                  type="checkbox"
                  checked={data.config.participantIds.includes(u.id)}
                  onChange={(e) =>
                    setData({
                      ...data,
                      config: {
                        ...data.config,
                        participantIds: e.target.checked
                          ? [...data.config.participantIds, u.id]
                          : data.config.participantIds.filter(
                              (id) => id !== u.id,
                            ),
                      },
                    })
                  }
                />
                {u.name}
              </label>
            ))}
          </fieldset>
          <Field
            label="Casas decimais exibidas"
            hint="Arredondamento de apresentação para o mais próximo, metade para cima. Os cálculos conservam a fração exata."
          >
            <input
              type="number"
              min={0}
              max={6}
              value={data.config.digits}
              onChange={(e) =>
                setData({
                  ...data,
                  config: { ...data.config, digits: Number(e.target.value) },
                })
              }
            />
          </Field>
          <p>
            Empates terão a mesma posição. Quem não participar continua
            conservando seu XP e suas recompensas.
          </p>
          <h3>Níveis por XP acumulado</h3>
          {data.config.levels.map((level, index) => (
            <div className="progress-level-edit" key={level.id}>
              <Field label="Nome">
                <input
                  required
                  value={level.name}
                  onChange={(e) =>
                    setData({
                      ...data,
                      config: {
                        ...data.config,
                        levels: data.config.levels.map((l, i) =>
                          i === index ? { ...l, name: e.target.value } : l,
                        ),
                      },
                    })
                  }
                />
              </Field>
              <Field label="XP mínimo">
                <input
                  required
                  inputMode="decimal"
                  value={level.xp}
                  onChange={(e) =>
                    setData({
                      ...data,
                      config: {
                        ...data.config,
                        levels: data.config.levels.map((l, i) =>
                          i === index
                            ? { ...l, xp: e.target.value.replace(",", ".") }
                            : l,
                        ),
                      },
                    })
                  }
                />
              </Field>
              <Field label="Cor">
                <input
                  type="color"
                  value={level.color}
                  onChange={(e) =>
                    setData({
                      ...data,
                      config: {
                        ...data.config,
                        levels: data.config.levels.map((l, i) =>
                          i === index ? { ...l, color: e.target.value } : l,
                        ),
                      },
                    })
                  }
                />
              </Field>
              <Button
                type="button"
                onClick={() =>
                  setData({
                    ...data,
                    config: {
                      ...data.config,
                      levels: data.config.levels.filter((_, i) => i !== index),
                    },
                  })
                }
              >
                Remover nível
              </Button>
            </div>
          ))}
          <div className="progress-actions">
            <Button
              type="button"
              onClick={() =>
                setData({
                  ...data,
                  config: {
                    ...data.config,
                    levels: [
                      ...data.config.levels,
                      {
                        id: crypto.randomUUID(),
                        name: "",
                        xp: "",
                        color: "#0a7775",
                      },
                    ],
                  },
                })
              }
            >
              Adicionar nível
            </Button>
            <Button type="submit" kind="primary" busy={busy}>
              Publicar regras
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}
type ProfileResponse = {
  achievements?: Array<{ name: string; at: string }>;
  profile: Profile;
  progress: ProgressRow;
  config: ProgressConfig;
  pending: number;
};
export function MyProfile({
  user,
  notify,
}: {
  user: User;
  notify: (s: string) => void;
}) {
  const [data, setData] = useState<ProfileResponse | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    const abort = new AbortController();
    api<ProfileResponse>("/progress/profile", "GET", undefined, abort.signal)
      .then(setData)
      .catch((e) => {
        if (!abort.signal.aborted) setError(e.message);
      });
    return () => abort.abort();
  }, [user.id]);
  async function photo(file: File) {
    try {
      if (file.size > 10 * 1024 * 1024)
        throw Error("Escolha uma foto menor que 10 MB.");
      const bitmap = await createImageBitmap(file);
      const canvas = document.createElement("canvas");
      canvas.width = 256;
      canvas.height = 256;
      const ctx = canvas.getContext("2d")!;
      const scale = Math.max(256 / bitmap.width, 256 / bitmap.height);
      ctx.drawImage(
        bitmap,
        (256 - bitmap.width * scale) / 2,
        (256 - bitmap.height * scale) / 2,
        bitmap.width * scale,
        bitmap.height * scale,
      );
      bitmap.close();
      setData((old) =>
        old
          ? {
              ...old,
              profile: {
                ...old.profile,
                avatar: canvas.toDataURL("image/jpeg", 0.8),
              },
            }
          : old,
      );
    } catch (e) {
      setError((e as Error).message);
    }
  }
  return (
    <section className="progress-page">
      <h1>Meu perfil</h1>
      <PushSettings />
      <OfflineSettings user={user} />
      {error && <Banner kind="danger">{error}</Banner>}
      {!data && !error && <p role="status">Consultando seu perfil…</p>}
      {data && (
        <form
          className="progress-card"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError("");
            try {
              const profile = await api<Profile>("/progress/profile", "PUT", {
                version: data.profile.version,
                bio: data.profile.bio,
                avatar: data.profile.avatar,
              });
              setData({ ...data, profile });
              notify("Perfil salvo.");
            } catch (e) {
              setError((e as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          <div className="progress-heading">
            {data.profile.avatar ? (
              <img
                className="progress-photo"
                src={data.profile.avatar}
                alt="Sua foto de perfil"
              />
            ) : (
              <span className="progress-avatar">{initials(user.name)}</span>
            )}
            <h2>{user.name}</h2>
          </div>
          <Field label="Foto">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => {
                if (e.target.files?.[0]) void photo(e.target.files[0]);
              }}
            />
          </Field>
          {data.profile.avatar && (
            <Button
              type="button"
              onClick={() =>
                setData({ ...data, profile: { ...data.profile, avatar: null } })
              }
            >
              Remover foto
            </Button>
          )}
          <Field label="Biografia">
            <textarea
              maxLength={600}
              rows={4}
              value={data.profile.bio}
              onChange={(e) =>
                setData({
                  ...data,
                  profile: { ...data.profile, bio: e.target.value },
                })
              }
            />
          </Field>
          <div className="progress-personal">
            <div>
              <span>Pontos registrados</span>
              <strong>
                <ExactCount
                  value={data.progress.points}
                  digits={data.config.digits}
                  scope={`profile:${data.profile.userId}:points`}
                  label="pontos"
                />
              </strong>
            </div>
            <div>
              <span>XP acumulado</span>
              <strong>
                <ExactCount
                  value={data.progress.xp}
                  digits={data.config.digits}
                  scope={`profile:${data.profile.userId}:xp`}
                  label="XP"
                />
              </strong>
            </div>
            <div>
              <span>Nível</span>
              <strong>{data.progress.level?.name ?? "A configurar"}</strong>
            </div>
          </div>
          {!!data.pending && (
            <Banner kind="warn">
              Há recompensas aguardando configuração. Os valores acima incluem
              somente concessões registradas.
            </Banner>
          )}
          <Button type="submit" kind="primary" busy={busy}>
            Salvar perfil
          </Button>
          <h3>Conquistas</h3>
          {data.achievements?.length ? (
            <ul>
              {data.achievements.map((a, i) => (
                <li key={i}>
                  ★ {a.name} · {new Date(a.at).toLocaleDateString("pt-BR")}
                </li>
              ))}
            </ul>
          ) : (
            <p>Suas conquistas aparecerão aqui quando forem concedidas.</p>
          )}
        </form>
      )}
    </section>
  );
}
export function PersonalProgressCard({ go }: { go: (page: string) => void }) {
  const [data, setData] = useState<ProfileResponse | null>(null),
    [error, setError] = useState(false);
  useEffect(() => {
    const abort = new AbortController();
    api<ProfileResponse>("/progress/profile", "GET", undefined, abort.signal)
      .then(setData)
      .catch(() => {
        if (!abort.signal.aborted) setError(true);
      });
    return () => abort.abort();
  }, []);
  return (
    <section className="home-progress-preview">
      <h2>Seu progresso</h2>
      {error ? (
        <p>Não foi possível consultar seu progresso.</p>
      ) : !data ? (
        <p role="status">Consultando progresso…</p>
      ) : (
        <>
          <div className="progress-personal">
            <div>
              <span>Pontos</span>
              <strong>
                {displayFraction(data.progress.points, data.config.digits)}
              </strong>
            </div>
            <div>
              <span>XP</span>
              <strong>
                {displayFraction(data.progress.xp, data.config.digits)}
              </strong>
            </div>
            <div>
              <span>Nível</span>
              <strong>{data.progress.level?.name ?? "A configurar"}</strong>
            </div>
          </div>
          {!!data.pending && (
            <p className="small muted">
              Há recompensas aguardando configuração.
            </p>
          )}
          <Button kind="ghost" onClick={() => go("rankings")}>
            Ver rankings
          </Button>
        </>
      )}
    </section>
  );
}
